import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Button } from '@mui/material';
import axios from 'axios';
import { Settings2, Save, X } from 'lucide-react';
import BotonAcceso from '../elementos/BotonAcceso';

const ConfigSedeTipos = ({ isOpen, onClose, sede }) => {
    const [tipos, setTipos] = useState([]);
    const [selectedTipos, setSelectedTipos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const apiBaseTipos = (process.env.REACT_APP_API_MENUSEMANALS || '/api/menus/menu-semanals/').replace(/\/menu-semanals\/?$/, '') + '/tipos-menu';
    const apiSedeTipos = (process.env.REACT_APP_API_SEDES || '/api/sedes/') + `${sede?.id}/tipos`;

    const fetchData = useCallback(async () => {
        if (!sede?.id) return;
        setLoading(true);
        try {
            const [resTipos, resSedeTipos] = await Promise.all([
                axios.get(apiBaseTipos),
                axios.get(apiSedeTipos)
            ]);
            setTipos(resTipos.data);
            setSelectedTipos(resSedeTipos.data.map(t => t.id));
        } catch (error) {
            console.error('Error al cargar configuración de sede:', error);
        } finally {
            setLoading(false);
        }
    }, [apiBaseTipos, apiSedeTipos, sede?.id]);

    useEffect(() => {
        if (isOpen && sede?.id) {
            fetchData();
        }
    }, [isOpen, sede, fetchData]);

    const handleToggle = (id) => {
        setSelectedTipos(prev =>
            prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.post(apiSedeTipos, { tiposIds: selectedTipos });
            onClose();
        } catch (error) {
            console.error('Error al guardar configuración:', error);
            alert('Error al guardar la configuración');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle className="bg-slate-900 text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Settings2 className="w-5 h-5 text-blue-400" />
                    <span className="font-black uppercase tracking-widest text-sm">Tipos de Menú: {sede?.nombre}</span>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </DialogTitle>
            <DialogContent className="bg-slate-950 p-6">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-6">
                    Selecciona los tipos de menú que se ofrecen habitualmente en esta sede.
                    Esto simplificará la carga de menús semanales.
                </p>
                {loading ? (
                    <div className="flex justify-center py-10">
                        <CircularProgress />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                        {tipos.map(tipo => (
                            <div
                                key={tipo.id}
                                onClick={() => handleToggle(tipo.id)}
                                className={`p-4 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3 ${selectedTipos.includes(tipo.id)
                                    ? "bg-blue-500/10 border-blue-500 text-blue-400"
                                    : "bg-slate-900 border-slate-800 text-slate-500 hover:border-slate-700"
                                    }`}
                            >
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${selectedTipos.includes(tipo.id) ? "bg-blue-500 border-blue-500" : "border-slate-700"
                                    }`}>
                                    {selectedTipos.includes(tipo.id) && <div className="w-2 h-2 bg-white rounded-sm" />}
                                </div>
                                <span className="font-black uppercase tracking-tight text-xs">{tipo.nombre}</span>
                            </div>
                        ))}
                    </div>
                )}
            </DialogContent>
            <DialogActions className="bg-slate-900 p-4 border-t border-slate-800">
                <Button
                    onClick={onClose}
                    className="text-slate-400 font-black text-xs uppercase tracking-widest"
                >
                    Cancelar
                </Button>
                <BotonAcceso
                    variant="accent"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-black text-[11px] uppercase tracking-widest px-6 py-2 rounded-xl shadow-lg shadow-blue-950/20 flex items-center gap-2 transform hover:scale-[1.02] transition-all"
                >
                    {saving ? <CircularProgress size={16} color="inherit" /> : <Save className="w-4 h-4" />}
                    Guardar Configuración
                </BotonAcceso>
            </DialogActions>
        </Dialog>
    );
};

export default ConfigSedeTipos;
